import React, { useEffect, useMemo, useState } from "react"
import { destroy, touch } from "redux-form"

import { LexicalComposer } from "@lexical/react/LexicalComposer"
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin"
import { ContentEditable } from "@lexical/react/LexicalContentEditable"
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin"
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary"
import EditorToolbar from "./components/EditorToolbar"
import editorTheme from "./themes"
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table"

import "./styles.less"
import { ToolbarContext } from "./components/EditorToolbar/ToolbarContext"
import { ListPlugin } from "@lexical/react/LexicalListPlugin"
import { ListItemNode, ListNode } from "@lexical/list"
import { LinkNode } from "@lexical/link"
import { CheckListPlugin } from "@lexical/react/LexicalCheckListPlugin"
import FloatingLinkEditorPlugin from "./components/FloatingLinkEditorPlugin"
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin"
import UpdateStatePlugin from "./components/UpdateStatePlugin"
import ImagesPlugin from "./components/ImagePlugin/ImagePlugin"
import { ImageNode } from "./components/ImagePlugin/ImageNode"
import { TablePlugin } from "@lexical/react/LexicalTablePlugin"
import TableCellResizerPlugin from "./components/TablePlugin/TableCellResizer"
import TableHoverActionsPlugin from "./components/TablePlugin/TableHoverActionsPlugin"
import TableActionMenuPlugin from "./components/TablePlugin/TableActionMenuPlugin"
import LexicalHtmlViewInputPlugin from "./components/LexicalHtmlViewInputPlugin"
import { ExtendedTextNode } from "./components/ExtendedTextNode/ExtendedTextNode"
import { TextNode } from "lexical"
import SetInitialDataPlugin from "./components/SetInitialDataPlugin"
import { modalRelatedFormNames } from "../../../components/AppRoutes"
import classNames from "classnames"
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin"
import FocusTrackerPlugin from "./components/FocusBlurTrackerPlugin/FocusBlurTrackerPlugin"

// Catch any errors that occur during Lexical updates and log them
// or throw them as needed. If you don't throw them, Lexical will
// try to recover gracefully without losing user data.
function onError(error: any) {
  console.error(error)
}

const LexicalEditor = (props: any) => {
  const [isLinkEditMode, setIsLinkEditMode] = useState<boolean>(false)
  const [isColorPickerOpen, setIsColorPickerOpen] = useState<boolean>(false)
  const [hasFocus, setHasFocus] = useState(false)
  const hasError = useMemo(() => props.meta.touched && props.meta.invalid, [props.meta])

  // store the previous data in this variable (updates oj editor blur) to compare with possible value changes from outside
  const [prevData, setPrevData] = useState(props?.input?.value)

  const initialConfig = {
    namespace: "MyEditor",
    theme: editorTheme,
    editable: !props.disabled,
    nodes: [
      ListNode,
      ListItemNode,
      LinkNode,
      ImageNode,
      TableNode,
      TableCellNode,
      TableRowNode,
      ExtendedTextNode,
      {
        replace: TextNode,
        with: (node: TextNode) => new ExtendedTextNode(node.__text),
        withKlass: ExtendedTextNode,
      },
    ],
    onError,
  }

  const [floatingAnchorElem, setFloatingAnchorElem] = useState<HTMLDivElement | undefined>(
    undefined
  )

  const editorRef = (_floatingAnchorElem: HTMLDivElement) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem)
    }
  }

  const placeholderValue = props?.placeholder || "Enter some text..."

  useEffect(() => {
    return () => {
      // Destroy the form in redux state on unmount, otherwise it is preserved there
      // modalRelatedFormNames forms are used in formWizard modal and are destroyed in AppRoutes on modal close
      // DocumentEditBlock component has a preview/editor switcher. For that component, skip the destroy part, otherwise it'll erase the form on every switch
      if (!modalRelatedFormNames.includes(props?.meta?.form) && !props.preserveFormOnUnmount) {
        props?.meta?.dispatch(destroy(props?.meta?.form))
      }
    }
  }, [])

  const handleFocus = (hasFocus: boolean) => {
    if (props?.meta && !hasFocus) {
      props.meta.dispatch(touch(props.meta.form, props.input.name))
    }
    setHasFocus(hasFocus)
  }

  return (
    <>
      <div
        className={classNames("LexicalEditor tw-flex tw-flex-col", props?.className, {
          "LexicalEditor-error": hasError,
        })}
      >
        <ToolbarContext>
          <LexicalComposer initialConfig={initialConfig}>
            <EditorToolbar
              setIsLinkEditMode={setIsLinkEditMode}
              disabled={props.disabled}
              value={props?.input?.value}
              setIsColorPickerOpen={setIsColorPickerOpen}
              className={props.toolbarClassName}
            />
            <RichTextPlugin
              contentEditable={
                <div
                  ref={editorRef}
                  className={"LexicalEditor__contentWrapper tw-flex tw-flex-col tw-flex-1"}
                >
                  <ContentEditable
                    disabled={props.disabled}
                    className={classNames(
                      "LexicalEditor__content tw-flex-1",
                      props.contentClassName
                    )}
                    aria-placeholder={placeholderValue}
                    placeholder={
                      <div className="LexicalEditor__placeholder">{placeholderValue}</div>
                    }
                  />
                  <LexicalHtmlViewInputPlugin disabled={props.disabled} />
                  <FocusTrackerPlugin setPrevData={setPrevData} setHasFocus={handleFocus} />
                </div>
              }
              ErrorBoundary={LexicalErrorBoundary}
            />
            <SetInitialDataPlugin
              initialData={props?.input?.value}
              hasFocus={hasFocus}
              prevData={prevData}
              isModalOpen={isLinkEditMode || isColorPickerOpen}
            />
            <FloatingLinkEditorPlugin
              anchorElem={floatingAnchorElem}
              isLinkEditMode={isLinkEditMode}
              setIsLinkEditMode={setIsLinkEditMode}
            />
            <TablePlugin hasCellMerge hasCellBackgroundColor hasHorizontalScroll />
            <TableCellResizerPlugin />
            <TableHoverActionsPlugin />
            <TableActionMenuPlugin cellMerge setIsColorPickerOpen={setIsColorPickerOpen} />
            <TabIndentationPlugin />
            <LinkPlugin />
            <ImagesPlugin />
            <ListPlugin />
            <CheckListPlugin />
            <HistoryPlugin />
            <UpdateStatePlugin
              onChange={props?.input?.onChange}
              prevData={prevData}
              setPrevData={setPrevData}
            />
          </LexicalComposer>
        </ToolbarContext>
      </div>
      {hasError && <p className="ant-form-item-explain-error">{props.meta.error}</p>}
    </>
  )
}

export default LexicalEditor
